/**
 * IncidentsScreen — lista de reportes + crear nuevo + ver detalle.
 * A = abrir detalle, B = back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import { incidentsApi, type Incident, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { WrenchSprite, LoadingSprite } from '@/components/Sprites';

function statusVariant(status: Incident['status']): 'success' | 'pending' | 'info' | 'danger' {
  switch (status) {
    case 'resolved':     return 'success';
    case 'in_progress':  return 'pending';
    case 'cancelled':    return 'info';
    default:             return 'danger'; // pending / open
  }
}

function statusShort(status: Incident['status']): string {
  return status === 'resolved' ? 'OK'
       : status === 'in_progress' ? '+'
       : status === 'cancelled' ? 'x'
       : '!';
}

export function IncidentsScreen() {
  const { goto, openDetail, openModal } = useApp();
  const [items, setItems] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(0);
  const handlersRef = useRef({ items, selected, openDetail, openModal });
  handlersRef.current = { items, selected, openDetail, openModal };

  const load = useCallback(async () => {
    try {
      const res = await incidentsApi.list();
      setItems(res.incidents || []);
    } catch (err) {
      if (err instanceof ApiError) console.error('Incidents load:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = (delta: number) => {
    if (items.length === 0) return;
    const next = Math.max(0, Math.min(items.length - 1, selected + delta));
    if (next !== selected) {
      setSelected(next);
      sfx.tick();
    }
  };

  useEffect(() => {
    const onUp = () => move(-1);
    const onDown = () => move(1);
    const onA = () => {
      const h = handlersRef.current;
      const item = h.items[h.selected];
      if (item) { sfx.confirm(); h.openDetail({ id: item.id, type: 'incident' }); }
    };
    const onB = () => { sfx.cancel(); goto('home'); };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [goto]);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <WrenchSprite size={11} />
          <span className="gb-pix gb-pix-xs">REPORTES · {items.length}</span>
        </div>
        <div className="gb-status-right">
          <button
            onClick={() => { sfx.tick(); openModal('create-incident'); }}
            className="gb-pix gb-pix-xs"
            style={{
              background: 'var(--gb-mid)',
              color: 'var(--gb-bg)',
              border: 'none',
              padding: '1px 6px',
              fontSize: 5,
              cursor: 'pointer',
              borderRadius: 2,
            }}
            data-testid="incidents-create"
          >
            + NUEVO
          </button>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '0', gap: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-xs gb-mid-text">CARGANDO...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <WrenchSprite size={48} />
            <div className="gb-pix gb-pix-xs gb-mid-text">SIN REPORTES</div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, maxWidth: 200, textAlign: 'center' }}>
              Reportá una gotera, problema eléctrico o cualquier cosa.
            </div>
          </div>
        ) : (
          <div className="gb-list" style={{ overflowY: 'auto', flex: 1 }}>
            {items.map((inc, i) => {
              const sel = i === selected;
              return (
                <button
                  key={inc.id}
                  className="gb-list-item"
                  data-selected={sel}
                  onClick={() => { setSelected(i); sfx.confirm(); openDetail({ id: inc.id, type: 'incident' }); }}
                  onMouseEnter={() => setSelected(i)}
                  data-testid={`incident-${inc.id}`}
                >
                  <div className="gb-list-row">
                    <span className={`gb-list-badge gb-list-badge--${statusVariant(inc.status)}`} style={{ fontSize: 5 }}>
                      {statusShort(inc.status)}
                    </span>
                    <span className="gb-list-title">{inc.title}</span>
                  </div>
                  <div className="gb-list-row gb-list-meta">
                    <span>{inc.category}</span>
                    <span>· {formatRelative(inc.created_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <div
          className="gb-pix gb-pix-xs gb-mid-text"
          style={{ fontSize: 5, textAlign: 'center', flexShrink: 0, paddingTop: 2 }}
        >
          [A] DETALLE · [B] VOLVER
        </div>
      </div>
    </div>
  );
}